"use client";

import { ArrowRight, BookOpenCheck, Play, Search } from "lucide-react";
import Link from "next/link";

type GuidesHeroProps = {
  guideCount: number;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
};

export function GuidesHero({ guideCount, searchTerm, onSearchTermChange }: GuidesHeroProps) {
  const hasGuides = guideCount > 0;

  return (
    <section
      className="relative isolate overflow-hidden bg-[#FBFEFA] px-4 pb-10 pt-12 text-slate-950 sm:px-6 sm:pb-12 sm:pt-16 lg:px-8"
      aria-labelledby="guides-heading"
    >
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-32 bg-gradient-to-b from-transparent to-white"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(135deg,rgba(8,166,47,0.07)_1px,transparent_1px)] bg-[length:34px_34px] opacity-70"
        aria-hidden
      />

      <div className="mx-auto max-w-[1100px] text-center">
        <p className="mx-auto inline-flex items-center gap-2 rounded-full border border-green-100 bg-white px-4 py-2 text-xs font-black text-[#087D2F] shadow-sm">
          <BookOpenCheck className="h-4 w-4" strokeWidth={2.5} aria-hidden />
          Teacher Guides
        </p>

        <h1
          id="guides-heading"
          className="mx-auto mt-6 max-w-[900px] text-4xl font-black leading-[1.04] text-slate-950 sm:text-5xl lg:text-6xl"
        >
          Practical teaching guides for Moroccan English classrooms
        </h1>

        <p className="mx-auto mt-5 max-w-[760px] text-lg font-medium leading-8 text-slate-700 sm:text-xl sm:leading-9">
          A growing library of lesson planning, staging, assessment, and classroom strategy guides built for
          Moroccan secondary English teachers.
        </p>

        <div className="mx-auto mt-8 max-w-[760px]">
          <label htmlFor="guides-search" className="sr-only">
            Search guides
          </label>
          <div className="flex min-h-14 items-center gap-3 rounded-full border border-slate-200 bg-white px-5 shadow-[0_22px_70px_-46px_rgba(15,23,42,0.48)] ring-1 ring-slate-100 focus-within:border-green-300 focus-within:ring-green-100">
            <Search className="h-5 w-5 shrink-0 text-[#08A62F]" strokeWidth={2.5} aria-hidden />
            <input
              id="guides-search"
              type="search"
              value={searchTerm}
              disabled={!hasGuides}
              onChange={(event) => onSearchTermChange(event.target.value)}
              placeholder="Search by level, skill, topic, or classroom problem..."
              className="min-h-14 min-w-0 flex-1 bg-transparent text-base font-semibold text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:text-slate-400"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            href="/sign-up"
            className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-full bg-[#08A62F] px-8 text-base font-bold text-white shadow-[0_18px_34px_-22px_rgba(8,166,47,0.95)] transition-colors duration-150 hover:bg-[#078F2B] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-green-700 sm:w-auto"
          >
            Start free for 7 days
            <ArrowRight className="h-5 w-5" strokeWidth={2.6} aria-hidden />
          </Link>
          <Link
            href="/#how-it-works"
            className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-7 text-base font-bold text-slate-950 shadow-sm transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-green-600 sm:w-auto"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-50 text-[#08A62F]">
              <Play className="ml-0.5 h-4 w-4 fill-current" strokeWidth={2.3} aria-hidden />
            </span>
            See how Darsi works
          </Link>
        </div>

        <p className="mt-5 text-sm font-bold text-slate-500">
          {hasGuides ? `${guideCount} guides available` : "Guides coming soon"}
        </p>
      </div>
    </section>
  );
}
